import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon, ArrowDownTrayIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { playersAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { getErrorMessage } from '../../utils/errorMessage';
import type { PlayerImportPreview, PlayerImportResult } from '../../types/player';

interface PlayerImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImported: () => void;
}

/**
 * Játékosok felvétele Excelből: minta letöltése, kitöltött fájl feltöltése,
 * előnézet a hibákkal, majd a kiválasztott sorok felvétele.
 */
const PlayerImportModal: React.FC<PlayerImportModalProps> = ({ isOpen, onClose, onImported }) => {
  const { showToast } = useToast();
  const [preview, setPreview] = useState<PlayerImportPreview | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [result, setResult] = useState<PlayerImportResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setPreview(null);
    setSelected(new Set());
    setResult(null);
    setError(null);
  };

  const close = () => {
    reset();
    onClose();
  };

  const handleTemplate = async () => {
    try {
      await playersAPI.downloadImportTemplate();
    } catch (err) {
      showToast(getErrorMessage(err, 'A minta letöltése nem sikerült.'), 'error');
    }
  };

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const data = await playersAPI.previewImport(file);
      setPreview(data);
      // Alapból a hibátlan, nem duplikált sorok vannak kijelölve
      setSelected(new Set(data.rows.filter((r) => r.errors.length === 0 && !r.isDuplicate).map((r) => r.rowNumber)));
    } catch (err) {
      setError(getErrorMessage(err, 'A fájlt nem sikerült beolvasni.'));
      setPreview(null);
    } finally {
      setBusy(false);
    }
  };

  const toggle = (rowNumber: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(rowNumber)) next.delete(rowNumber);
      else next.add(rowNumber);
      return next;
    });

  const handleImport = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const rows = preview.rows.filter((r) => selected.has(r.rowNumber));
      const res = await playersAPI.import(rows);
      setResult(res);
      setPreview(null);
      if (res.createdCount > 0) {
        showToast(`${res.createdCount} játékos felvéve.`);
        onImported();
      }
    } catch (err) {
      setError(getErrorMessage(err, 'A felvétel nem sikerült.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={close} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex min-h-full items-stretch justify-center md:items-center md:p-4">
        <Dialog.Panel className="modal-panel-mobile flex w-full flex-col bg-white shadow-xl md:max-h-[90vh] md:max-w-4xl md:rounded-lg">
          <div className="flex shrink-0 items-center justify-between border-b p-4 md:p-6">
            <Dialog.Title className="text-lg font-semibold text-gray-900">Játékosok importálása Excelből</Dialog.Title>
            <button onClick={close} className="flex h-9 w-9 items-center justify-center rounded-full text-gray-400 hover:text-gray-600">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="flex-1 space-y-4 overflow-y-auto p-4 md:p-6">
            {error && <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}

            {result && (
              <div className="space-y-2 rounded-lg border border-green-200 bg-green-50 p-4 text-sm">
                <p className="font-semibold text-green-800">
                  {result.createdCount} játékos felvéve{result.skippedCount > 0 ? `, ${result.skippedCount} kihagyva` : ''}.
                </p>
                {result.messages.length > 0 && (
                  <ul className="list-disc pl-5 text-gray-700">
                    {result.messages.map((m, i) => (
                      <li key={i}>{m}</li>
                    ))}
                  </ul>
                )}
                <p className="text-gray-600">
                  A szülőket a „Meghívók kiküldése” gombbal hívhatod meg, ha még nincs fiókjuk.
                </p>
              </div>
            )}

            {!preview && (
              <ol className="list-decimal space-y-3 pl-5 text-sm text-gray-700">
                <li>
                  Töltsd le a mintát, és másold bele a játékosokat (egy sor = egy játékos).
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={handleTemplate}
                      className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      Minta letöltése
                    </button>
                  </div>
                </li>
                <li>
                  Töltsd fel a kitöltött fájlt - előbb megmutatjuk, mi kerülne be, és csak a jóváhagyásod után veszünk fel bárkit.
                  <div className="mt-2">
                    <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700">
                      <ArrowUpTrayIcon className="h-4 w-4" />
                      {busy ? 'Beolvasás...' : 'Fájl kiválasztása (.xlsx)'}
                      <input
                        type="file"
                        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        className="hidden"
                        disabled={busy}
                        onChange={(e) => {
                          handleFile(e.target.files?.[0]);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>
                </li>
              </ol>
            )}

            {preview && (
              <>
                <div className="flex flex-wrap gap-2 text-sm">
                  <span className="rounded-full bg-green-100 px-3 py-1 text-green-800">{preview.validCount} felvehető</span>
                  {preview.duplicateCount > 0 && (
                    <span className="rounded-full bg-gray-100 px-3 py-1 text-gray-700">{preview.duplicateCount} már létezik</span>
                  )}
                  {preview.errorCount > 0 && (
                    <span className="rounded-full bg-red-100 px-3 py-1 text-red-800">{preview.errorCount} hibás</span>
                  )}
                </div>

                <div className="overflow-x-auto rounded-lg border">
                  <table className="w-full min-w-[640px] divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50 text-left text-xs font-medium uppercase text-gray-500">
                      <tr>
                        <th className="w-10 px-3 py-2" />
                        <th className="px-3 py-2">Sor</th>
                        <th className="px-3 py-2">Név</th>
                        <th className="px-3 py-2">Születés</th>
                        <th className="px-3 py-2">Korosztály</th>
                        <th className="px-3 py-2">Szülő</th>
                        <th className="px-3 py-2">Megjegyzés</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {preview.rows.map((row) => {
                        const blocked = row.errors.length > 0;
                        return (
                          <tr key={row.rowNumber} className={blocked ? 'bg-red-50/50' : row.isDuplicate ? 'bg-gray-50' : ''}>
                            <td className="px-3 py-2">
                              <input
                                type="checkbox"
                                disabled={blocked || row.isDuplicate}
                                checked={selected.has(row.rowNumber)}
                                onChange={() => toggle(row.rowNumber)}
                                className="h-4 w-4 rounded border-gray-300 text-primary-600"
                              />
                            </td>
                            <td className="px-3 py-2 text-gray-500">{row.rowNumber}.</td>
                            <td className="px-3 py-2 font-medium text-gray-900">{row.name || '-'}</td>
                            <td className="px-3 py-2 text-gray-700">
                              {row.birth ? new Date(row.birth).toLocaleDateString('hu-HU') : '-'}
                              {row.gender && <span className="text-gray-400"> · {row.gender}</span>}
                            </td>
                            <td className="px-3 py-2 text-gray-700">{row.ageGroupName || '-'}</td>
                            <td className="px-3 py-2 text-gray-700">{row.parentUserName ?? row.parentEmail ?? '-'}</td>
                            <td className="px-3 py-2 text-xs">
                              {row.isDuplicate && !blocked && <div className="text-gray-600">Már létezik - kihagyjuk.</div>}
                              {row.errors.map((e, i) => (
                                <div key={`e${i}`} className="text-red-700">{e}</div>
                              ))}
                              {row.warnings.map((w, i) => (
                                <div key={`w${i}`} className="text-amber-700">{w}</div>
                              ))}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>

          <div className="flex shrink-0 flex-col-reverse gap-2 border-t p-4 pb-safe-4 md:flex-row md:justify-end md:p-6">
            {preview ? (
              <>
                <button type="button" onClick={reset} className="rounded-md bg-gray-100 px-4 py-2 text-gray-700 hover:bg-gray-200">
                  Másik fájl
                </button>
                <button
                  type="button"
                  disabled={busy || selected.size === 0}
                  onClick={handleImport}
                  className="rounded-md bg-primary-600 px-4 py-2 font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  {busy ? 'Felvétel...' : `${selected.size} játékos felvétele`}
                </button>
              </>
            ) : (
              <button type="button" onClick={close} className="rounded-md bg-gray-100 px-4 py-2 text-gray-700 hover:bg-gray-200">
                Bezárás
              </button>
            )}
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default PlayerImportModal;
